import { useQuery } from "@tanstack/react-query";
import { ApiClientConfig } from "../../../../../auth/ApiClientConfig";
import type { OrderDrawerIdentifier } from "../components/order-drawer/types";

interface OrderApprovalHistoryStep {
  status?: string;
  statusCode?: string;
  date?: string;
  approver?: string;
  comments?: string;
}

interface OrderApprovalHistoryResponse {
  hasApprovalWorkflow?: boolean;
  canReview?: boolean;
  pendingApprovers?: string[];
  steps?: OrderApprovalHistoryStep[];
}

const EMPTY_APPROVAL_HISTORY: Required<OrderApprovalHistoryResponse> = {
  hasApprovalWorkflow: false,
  canReview: false,
  pendingApprovers: [],
  steps: [],
};

interface UseGetOrderApprovalHistoryOptions {
  identifier: OrderDrawerIdentifier | null;
  enabled: boolean;
}

export function useGetOrderApprovalHistory({
  identifier,
  enabled,
}: UseGetOrderApprovalHistoryOptions) {
  const biOrderId = identifier?.biOrderId;
  const webOrderId = identifier?.webOrderId;
  const isWebOrder = !biOrderId || biOrderId <= 0;
  const canFetch = enabled && isWebOrder && !!webOrderId && webOrderId > 0;

  const query = useQuery({
    queryKey: ["order-drawer-approval-history", webOrderId ?? null],
    queryFn: async (): Promise<OrderApprovalHistoryResponse> => {
      if (!webOrderId || webOrderId <= 0) {
        return EMPTY_APPROVAL_HISTORY;
      }

      const authorization = await ApiClientConfig.getAuthorization();
      const additionalHeaders = ApiClientConfig.getAdditionalHeaders();
      const headers = new Headers(additionalHeaders);

      headers.set("Authorization", authorization);
      headers.set("Accept", "application/json");

      const response = await fetch(
        `${import.meta.env.API_ENDPOINT}/api/OrderApprovalHistory/web/${webOrderId}`,
        {
          method: "GET",
          headers,
        },
      );

      if (!response.ok) {
        throw new Error("Unexpected server error while loading order approval history.");
      }

      return (await response.json()) as OrderApprovalHistoryResponse;
    },
    enabled: canFetch,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const normalizedHistory: Required<OrderApprovalHistoryResponse> = {
    hasApprovalWorkflow: query.data?.hasApprovalWorkflow ?? false,
    canReview: query.data?.canReview ?? false,
    pendingApprovers: query.data?.pendingApprovers ?? [],
    steps: query.data?.steps ?? [],
  };

  return {
    approvalHistory: normalizedHistory,
    isLoading: query.isFetching,
    isError: query.isError,
  };
}
